import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Plus, TrendingUp, DollarSign, Clock, CheckCircle,
  ArrowRight, Loader2, Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import InvoicePortalLayout from '@/components/invoice/InvoicePortalLayout';

const InvoicePortalDashboard = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [stats, setStats] = useState({ total: 0, issued: 0, paid: 0, totalAmount: 0 });
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) {
        navigate('/invoices/setup');
        return;
      }
      const s = shopsRes.data[0];
      setShop(s);

      const invRes = await api.get(`/shops/${s.id}/invoices`);
      const invoices = invRes.data;
      setRecentInvoices(invoices.slice(0, 5));
      setStats({
        total: invoices.length,
        issued: invoices.filter(i => i.status === 'issued').length,
        paid: invoices.filter(i => i.status === 'paid').length,
        totalAmount: invoices.filter(i => i.status !== 'cancelled').reduce((sum, i) => sum + (i.total || 0), 0),
      });
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: t('inv_portal_total_invoices') || 'Total Invoices', value: stats.total, icon: FileText, color: 'bg-blue-50 text-blue-600' },
    { label: t('inv_portal_pending') || 'Pending', value: stats.issued, icon: Clock, color: 'bg-amber-50 text-amber-600' },
    { label: t('inv_portal_paid') || 'Paid', value: stats.paid, icon: CheckCircle, color: 'bg-green-50 text-green-600' },
    { label: t('inv_portal_revenue') || 'Revenue', value: `€${stats.totalAmount.toFixed(2)}`, icon: DollarSign, color: 'bg-[#1e3a5f]/5 text-[#1e3a5f]' },
  ];

  if (loading) {
    return (
      <InvoicePortalLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </InvoicePortalLayout>
    );
  }

  return (
    <InvoicePortalLayout>
      <div className="space-y-6" data-testid="invoice-portal-dashboard">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">
              {t('inv_portal_welcome') || 'Welcome'}, {user?.name?.split(' ')[0] || 'User'}
            </h2>
            <p className="text-slate-500 text-sm">
              {shop?.business_name || shop?.name || ''} · {t('inv_portal_subtitle') || 'VeriFactu Invoice Portal'}
            </p>
          </div>
          <Link to="/invoices/manage"
            className="vf-btn-primary flex items-center gap-2" data-testid="portal-create-invoice">
            <Plus className="w-5 h-5" /> {t('inv_create') || 'New Invoice'}
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white border-2 border-slate-100 p-4" data-testid={`stat-card-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${card.color}`}>
                  <card.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">{card.value}</p>
              <p className="text-sm text-slate-500">{card.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Recent Invoices */}
        <div className="bg-white border-2 border-slate-100">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-[#1e3a5f]">{t('inv_portal_recent') || 'Recent Invoices'}</h3>
            <Link to="/invoices/manage" className="text-sm text-[#22c55e] hover:underline flex items-center gap-1">
              {t('inv_portal_view_all') || 'View All'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {recentInvoices.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">{t('inv_no_invoices') || 'No invoices yet'}</p>
              <Link to="/invoices/manage" className="text-sm text-[#22c55e] hover:underline mt-2 inline-block">
                {t('inv_create_first') || 'Create your first invoice'}
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {recentInvoices.map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1e3a5f]/5 rounded-sm flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <div>
                      <p className="font-mono font-semibold text-[#1e3a5f] text-sm">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-400">{inv.customer?.name} · {inv.issue_date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#22c55e]">€{inv.total?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${
                      inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                      inv.status === 'issued' ? 'bg-blue-100 text-blue-700' :
                      inv.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'
                    }`}>{inv.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Business Info */}
        {shop && (
          <div className="bg-white border-2 border-slate-100 p-4">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="w-5 h-5 text-[#1e3a5f]" />
              <h3 className="font-semibold text-[#1e3a5f]">{t('inv_portal_business') || 'Business Details'}</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-slate-400 text-xs">{t('inv_portal_biz_name') || 'Business Name'}</p><p className="font-medium text-[#1e3a5f]">{shop.business_name || shop.name}</p></div>
              <div><p className="text-slate-400 text-xs">NIF</p><p className="font-medium text-[#1e3a5f]">{shop.nif || '—'}</p></div>
              <div><p className="text-slate-400 text-xs">{t('inv_customer_address') || 'Address'}</p><p className="font-medium text-[#1e3a5f]">{shop.address}, {shop.city}</p></div>
              <div><p className="text-slate-400 text-xs">{t('inv_customer_email') || 'Email'}</p><p className="font-medium text-[#1e3a5f]">{shop.email}</p></div>
            </div>
          </div>
        )}
      </div>
    </InvoicePortalLayout>
  );
};

export default InvoicePortalDashboard;
